import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  message,
} from 'antd';
import type { RoleFormProps, RoleFormData, RolePortal } from '../types';
import { formatRoleCode, validateRoleCodeFormat, isSystemRole } from '../utils';

const { TextArea } = Input;

// REQ-20260521-0007: 角色可访问门户选项。
// 仅暴露 admin / mall（platform 是平台保留字，租户端 UI 不开放）。
const PORTAL_OPTIONS: { label: string; value: RolePortal }[] = [
  { label: '管理后台 (admin)', value: 'admin' },
  { label: '采购商城 (mall)', value: 'mall' },
];

export const RoleForm: React.FC<RoleFormProps> = ({
  open,
  mode,
  initialValues,
  loading = false,
  existingCodes = [],
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm<RoleFormData>();

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // 自动格式化角色代码
      values.code = formatRoleCode(values.code);

      await onOk(values);
      form.resetFields();
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleCodeBlur = () => {
    const code = form.getFieldValue('code');
    if (code) {
      form.setFieldsValue({ code: formatRoleCode(code) });
    }
  };

  return (
    <Modal
      title={mode === 'create' ? '新建角色' : '编辑角色'}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      destroyOnHidden
      data-testid="role-modal-form"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: 'custom',
          status: 'active',
          // REQ-20260521-0007: 默认勾选「管理后台」，与后端 fnd_roles.portal_access DEFAULT 对齐
          portalAccess: ['admin'],
        }}
      >
        <Form.Item
          name="name"
          label="角色名称"
          rules={[
            { required: true, message: '请输入角色名称' },
            { min: 2, max: 20, message: '角色名称长度为2-20个字符' },
          ]}
        >
          <Input placeholder="请输入角色名称" data-testid="role-input-name" />
        </Form.Item>

        <Form.Item
          name="code"
          label="角色代码"
          rules={[
            { required: true, message: '请输入角色代码' },
            {
              validator: async (_, value) => {
                if (!value) return;

                const formatted = formatRoleCode(value);

                if (!validateRoleCodeFormat(formatted)) {
                  throw new Error('角色代码只能包含大写字母、数字和下划线');
                }

                // 检查唯一性（编辑模式排除当前角色）
                if (mode === 'create' && existingCodes.includes(formatted)) {
                  throw new Error('该角色代码已存在');
                }
              },
            },
          ]}
          tooltip="大写字母、数字和下划线，自动添加ROLE_前缀"
        >
          <Input
            placeholder="例如：ADMIN"
            onBlur={handleCodeBlur}
            style={{ fontFamily: 'monospace' }}
            data-testid="role-input-code"
          />
        </Form.Item>

        <Form.Item
          name="type"
          label="角色类型"
        >
          <Radio.Group
            disabled={
              mode === 'edit' &&
              initialValues &&
              isSystemRole(initialValues.type!)
            }
            data-testid="role-radio-type"
          >
            <Radio value="system">系统角色</Radio>
            <Radio value="custom">自定义角色</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="description"
          label="角色描述"
          rules={[{ max: 200, message: '描述最多200个字符' }]}
        >
          <TextArea
            rows={4}
            placeholder="请输入角色描述"
            showCount
            maxLength={200}
            data-testid="role-input-description"
          />
        </Form.Item>

        {/* REQ-20260521-0007: 角色可访问门户。空选 → 后端兜底 ["admin"]，登录时与其他角色 union 写入 JWT available_portals。 */}
        <Form.Item
          name="portalAccess"
          label="可访问门户"
          tooltip="选择该角色允许访问的门户；勾选「采购商城」后绑定该角色的用户登录后可进入 /mall。不选则后端兜底为「管理后台」。"
          rules={[
            {
              validator: async (_, value) => {
                if (!value || !Array.isArray(value)) return;
                for (const v of value) {
                  if (v !== 'admin' && v !== 'mall') {
                    throw new Error('仅允许选择 admin / mall');
                  }
                }
              },
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="选择角色可访问门户"
            options={PORTAL_OPTIONS}
            data-testid="role-select-portal-access"
            allowClear
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="status" label="状态">
          <Radio.Group data-testid="role-radio-status">
            <Radio value="active">启用</Radio>
            <Radio value="disabled">禁用</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};
